const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3002');

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
    returned: number;
  };
  message?: string;
  error?: string;
  // Multi-workspace response fields
  currentWorkspaceId?: string;
}

class ApiService {
  // Prevent concurrent refresh attempts
  private refreshPromise: Promise<string> | null = null;

  private getAuthToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Attempt to refresh the expired token.
   * Returns the new token on success, throws on failure.
   * Multiple concurrent calls share the same promise to avoid race conditions.
   */
  private async attemptTokenRefresh(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      const expiredToken = localStorage.getItem('token');
      if (!expiredToken) {
        throw new Error('No token to refresh');
      }

      console.log('🔄 API: Attempting silent token refresh...');
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${expiredToken}`,
        },
      });

      const data: ApiResponse<{ token: string }> = await response.json();

      if (!response.ok || !data.success || !data.data?.token) {
        throw new Error(data.error || 'Token refresh failed');
      }

      const newToken = data.data.token;
      localStorage.setItem('token', newToken);
      console.log('✅ API: Token refreshed silently');
      return newToken;
    })().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = this.getAuthToken();

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      // 🛡️ AUTO-REFRESH: If token expired, try to refresh and retry once
      if (response.status === 401) {
        if (token && !isRetry) {
          // Don't try to refresh the refresh endpoint itself (avoid loops)
          if (!endpoint.includes('/auth/refresh')) {
            try {
              const newToken = await this.attemptTokenRefresh();
              // Retry the original request with the new token
              return this.request<T>(endpoint, {
                ...options,
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${newToken}`,
                  ...(options.headers || {}),
                },
              }, true /* isRetry */);
            } catch (refreshError) {
              console.warn('🔒 API: Token refresh failed, forcing logout:', refreshError);
            }
          }

          // Refresh failed or not applicable – force logout
          localStorage.removeItem('token');
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }

        // Silent 401 for pre-login checks (no token)
        if (!token) {
          console.log('🔇 API: Skipping 401 error (no token - pre-login state)');
        }
        
        throw new Error('Access token required');
      }
      
      const data: ApiResponse<T> = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      if (!data.success) {
        throw new Error(data.error || 'API request failed');
      }

      return data.data as T;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Auth methods
  async login(email: string, password: string) {
    const response = await this.request<{ user: any; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // Store token in localStorage
    if (response.token) {
      localStorage.setItem('token', response.token);
    }

    return response;
  }

  async register(name: string, email: string, password: string, companyWebsite?: string) {
    const response = await this.request<{ user: any; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, companyWebsite }),
    });

    // Store token in localStorage
    if (response.token) {
      localStorage.setItem('token', response.token);
    }

    return response;
  }

  async getCurrentUser() {
    const response = await this.request<{ user: any }>('/auth/me');
    return response.user;
  }

  async refreshToken() {
    const response = await this.request<{ token: string }>('/auth/refresh', {
      method: 'POST',
    });

    if (response.token) {
      localStorage.setItem('token', response.token);
    }

    return response;
  }

  logout() {
    localStorage.removeItem('token');
  }

  async forgotPassword(email: string) {
    const url = `${API_BASE_URL}/auth/forgot-password`;
    const token = this.getAuthToken();

    const config: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ email }),
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Forgot password request failed:', error);
      throw error;
    }
  }

  // Projects methods
  async getProjects() {
    return this.request<any[]>('/projects');
  }

  async getProject(id: string) {
    return this.request<any>(`/projects/${id}`);
  }

  async createProject(project: { name: string; description: string; categoryType: string; categories?: string[] }) {
    return this.request<any>('/projects', {
      method: 'POST',
      body: JSON.stringify(project),
    });
  }

  async updateProject(id: string, updates: { name?: string; description?: string; categoryType?: string; categories?: string[] }) {
    return this.request<any>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteProject(id: string) {
    return this.request<void>(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  async forceDeleteProject(id: string) {
    return this.request<void>(`/projects/${id}/force`, {
      method: 'DELETE',
    });
  }

  async deleteProjectOnly(id: string) {
    return this.request<void>(`/projects/${id}/only`, {
      method: 'DELETE',
    });
  }

  // Project members methods
  async getProjectMembers(projectId: string) {
    console.log('🌐 API: Requesting project members for:', projectId);
    const result = await this.request<any[]>(`/projects/${projectId}/members`);
    console.log('🌐 API: Project members response:', result);
    return result;
  }

  async addProjectMember(projectId: string, memberId: string, accessLevel: string = 'read') {
    console.log('🌐 API: Adding project member...', { projectId, memberId, accessLevel });
    const result = await this.request<any>(`/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify({ memberId, accessLevel }),
    });
    console.log('🌐 API: Add project member response:', result);
    return result;
  }

  async removeProjectMember(projectId: string, memberId: string) {
    console.log('🌐 API: Removing project member...', { projectId, memberId });
    await this.request<void>(`/projects/${projectId}/members/${memberId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Project member removed successfully');
  }

  // Time entries methods
  async getTimeEntries() {
    return this.request<any[]>('/time-entries');
  }

  async createTimeEntry(entry: any) {
    return this.request<any>('/time-entries', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  }

  async updateTimeEntry(id: string, updates: any) {
    return this.request<any>(`/time-entries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteTimeEntry(id: string) {
    return this.request<void>(`/time-entries/${id}`, {
      method: 'DELETE',
    });
  }

  // Dashboard methods
  async getDashboardStats() {
    return this.request<any>('/time-entries/dashboard/stats');
  }

  async getRecentActivity() {
    return this.request<any[]>('/dashboard/recent-activity');
  }

  async getActiveTimeEntry() {
    return this.request<any>('/time-entries/active');
  }

  // Reports methods
  async getReports(timeRange: string = '30d', groupBy: string = 'project') {
    return this.request<any>(`/time-entries/reports?timeRange=${timeRange}&groupBy=${groupBy}`);
  }

  // AI methods
  async getAISettings() {
    return this.request<any>('/ai/settings');
  }

  async updateAISettings(settings: any) {
    return this.request<any>('/ai/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getScreenshots(params?: { date?: string; project?: string; limit?: number; offset?: number; timeRange?: string; startDate?: string; endDate?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.date) queryParams.append('date', params.date);
    if (params?.project) queryParams.append('project', params.project);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.timeRange) queryParams.append('timeRange', params.timeRange);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const query = queryParams.toString();
    console.log('🌐 API: getScreenshots params:', params);
    
    const url = `/ai/screenshots${query ? `?${query}` : ''}`;
    const token = this.getAuthToken();

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    };

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, config);
      const data: ApiResponse<any> = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to get screenshots');
      }

      // Return both data and pagination info
      return {
        data: data.data || [],
        pagination: data.pagination || { total: 0, limit: 0, offset: 0, hasMore: false, returned: 0 }
      };
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async uploadScreenshot(file: File) {
    const formData = new FormData();
    formData.append('screenshot', file);

    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/ai/screenshots/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data: ApiResponse = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Upload failed');
    }

    return data.data;
  }

  async uploadScreenshotWithMetadata(file: File, metadata: any) {
    const formData = new FormData();
    formData.append('screenshot', file);
    formData.append('metadata', JSON.stringify(metadata));

    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/ai/screenshots/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data: ApiResponse = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Upload failed');
    }

    return data.data;
  }

  async analyzeScreenshot(file: File) {
    const formData = new FormData();
    formData.append('screenshot', file);

    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/ai/screenshots/analyze`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data: ApiResponse = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Analysis failed');
    }

    return data.data;
  }

  async deleteScreenshot(id: string) {
    return this.request<void>(`/ai/screenshots/${id}`, {
      method: 'DELETE',
    });
  }

  async getAnalytics(params?: { timeRange?: string; date?: string; startDate?: string; endDate?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.timeRange) queryParams.append('timeRange', params.timeRange);
    if (params?.date) queryParams.append('date', params.date);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const query = queryParams.toString();
    console.log('🌐 API: getAnalytics params:', params);
    console.log('🌐 API: getAnalytics query string:', query);
    return this.request<any>(`/ai/analytics${query ? `?${query}` : ''}`);
  }

  async getSuggestedTimeEntries(date?: string) {
    const query = date ? `?date=${date}` : '';
    return this.request<any>(`/ai/suggested-time-entries${query}`);
  }

  async generateTimeEntriesFromScreenshots(date?: string, projectId?: string) {
    return this.request<any>('/ai/generate-time-entries', {
      method: 'POST',
      body: JSON.stringify({ date, projectId }),
    });
  }

  async markScreenshotsAsProcessed(screenshotIds: string[], timeEntryId?: string) {
    return this.request<any>('/ai/mark-screenshots-processed', {
      method: 'POST',
      body: JSON.stringify({ screenshotIds, timeEntryId }),
    });
  }

  // Token usage methods
  async getTokenStats(timeRange: string = '7d') {
    console.log('🌐 API: Requesting token stats for timeRange:', timeRange);
    const result = await this.request<any>(`/tokens/stats?timeRange=${timeRange}`);
    console.log('🌐 API: Token stats response:', result);
    return result;
  }

  // Team methods
  async getTeamMembers(weekStart?: string) {
    console.log('🌐 API: Requesting team members...');
    const query = weekStart ? `?weekStart=${weekStart}` : '';
    const result = await this.request<any[]>(`/team/members${query}`);
    console.log('🌐 API: Team members response:', result);
    return result;
  }

  async getTeamStats(weekStart?: string) {
    const query = weekStart ? `?weekStart=${weekStart}` : '';
    return this.request<any>(`/team/stats${query}`);
  }

  async updateTeamMember(memberId: string, data: { 
    role?: string; 
    costRate?: number | null; 
    billRate?: number | null; 
    currency?: string;
  }) {
    console.log('🌐 API: Updating team member...', { memberId, data });
    return this.request<any>(`/team/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateMemberCapacity(memberId: string, capacity: number) {
    return this.request<any>(`/team/members/${memberId}/capacity`, {
      method: 'PUT',
      body: JSON.stringify({ capacity }),
    });
  }

  async inviteTeamMember(email: string, role: string, capacity: number) {
    return this.request<any>('/team/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role, capacity }),
    });
  }

  async getInvitationInfo(token: string) {
    console.log('🌐 API: Getting invitation info for token:', token);
    const result = await this.request<any>(`/team/invitation-info?token=${token}`, {
      method: 'GET',
    });
    console.log('🌐 API: Invitation info response:', result);
    return result;
  }

  async acceptTeamInvitation(token: string) {
    console.log('🌐 API: Accepting team invitation with token:', token);
    const result = await this.request<any>('/team/accept-invitation', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    console.log('🌐 API: Accept invitation response:', result);
    return result;
  }

  async getPendingInvitations() {
    console.log('🌐 API: Requesting pending invitations...');
    const result = await this.request<any[]>('/team/pending-invitations');
    console.log('🌐 API: Pending invitations response:', result);
    return result;
  }

  async resendInvitation(invitationId: string) {
    console.log('🌐 API: Resending invitation:', invitationId);
    const result = await this.request<any>('/team/resend-invitation', {
      method: 'POST',
      body: JSON.stringify({ invitationId }),
    });
    console.log('🌐 API: Resend invitation response:', result);
    return result;
  }

  async cancelInvitation(invitationId: string) {
    console.log('🌐 API: Cancelling invitation:', invitationId);
    const result = await this.request<void>(`/team/cancel-invitation/${invitationId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Cancel invitation response:', result);
    return result;
  }

  // Channel management methods
  async getChannels() {
    return this.request<any[]>('/team/channels');
  }

  async createChannel(channel: { name: string; description?: string; type: string }) {
    return this.request<any>('/team/channels', {
      method: 'POST',
      body: JSON.stringify(channel),
    });
  }

  async getChannelMembers(channelId: string) {
    return this.request<any[]>(`/team/channels/${channelId}/members`);
  }

  async inviteToChannel(channelId: string, email: string, role: string = 'member') {
    return this.request<any>(`/team/channels/${channelId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async removeFromChannel(channelId: string, memberId: string) {
    return this.request<void>(`/team/channels/${channelId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async updateChannelMemberRole(channelId: string, memberId: string, role: string) {
    return this.request<any>(`/team/channels/${channelId}/members/${memberId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // Categories methods
  async getCategories(): Promise<string[]> {
    console.log('🌐 API: Requesting categories...');
    const result = await this.request<string[]>('/categories');
    console.log('🌐 API: Categories response:', result);
    return result;
  }

  async createCategory(name: string): Promise<any> {
    return this.request<any>('/categories', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async deleteCategory(name: string): Promise<void> {
    return this.request<void>(`/categories/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }

  // Clients methods
  async getClients(): Promise<any[]> {
    console.log('🌐 API: Requesting clients...');
    const result = await this.request<any[]>('/clients');
    console.log('🌐 API: Clients response:', result);
    return result;
  }

  async createClient(client: {
    name: string;
    email?: string;
    company?: string;
    phone?: string;
    address?: string;
  }): Promise<any> {
    return this.request<any>('/clients', {
      method: 'POST',
      body: JSON.stringify(client),
    });
  }

  async updateClient(id: string, client: {
    name: string;
    email?: string;
    company?: string;
    phone?: string;
    address?: string;
  }): Promise<any> {
    return this.request<any>(`/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(client),
    });
  }

  async deleteClient(id: string): Promise<void> {
    return this.request<void>(`/clients/${id}`, {
      method: 'DELETE',
    });
  }

  async getClient(id: string): Promise<any> {
    return this.request<any>(`/clients/${id}`);
  }

  // Expenses methods
  async getExpenses(): Promise<any[]> {
    console.log('🌐 API: Requesting expenses...');
    const result = await this.request<any[]>('/expenses');
    console.log('🌐 API: Expenses response:', result);
    return result;
  }

  async createExpense(expense: {
    amount: number;
    currency: string;
    description: string;
    category: string;
    date: string;
    projectId?: string;
    clientId?: string;
    notes?: string;
  }): Promise<any> {
    return this.request<any>('/expenses', {
      method: 'POST',
      body: JSON.stringify(expense),
    });
  }

  async updateExpense(id: string, expense: {
    amount: number;
    currency: string;
    description: string;
    category: string;
    date: string;
    projectId?: string;
    clientId?: string;
    notes?: string;
  }): Promise<any> {
    return this.request<any>(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expense),
    });
  }

  async deleteExpense(id: string): Promise<void> {
    return this.request<void>(`/expenses/${id}`, {
      method: 'DELETE',
    });
  }

  async getExpense(id: string): Promise<any> {
    return this.request<any>(`/expenses/${id}`);
  }

  // Profile methods
  async getProfile(): Promise<any> {
    const result = await this.request<any>('/profile');
    return result;
  }

  async updateProfile(profile: {
    name: string;
    email: string;
    phone?: string;
    jobTitle?: string;
    department?: string;
    bio?: string;
  }): Promise<any> {
    console.log('🌐 API: Updating user profile...', profile);
    const result = await this.request<any>('/profile', {
      method: 'PUT',
      body: JSON.stringify(profile),
    });
    console.log('🌐 API: Profile update response:', result);
    return result;
  }

  async uploadAvatar(file: File): Promise<any> {
    console.log('🌐 API: Uploading avatar...');
    const formData = new FormData();
    formData.append('avatar', file);

    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/profile/avatar`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data: ApiResponse = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Avatar upload failed');
    }

    console.log('🌐 API: Avatar upload response:', data.data);
    return data.data;
  }

  async deleteAvatar(): Promise<void> {
    console.log('🌐 API: Deleting avatar...');
    await this.request<void>('/profile/avatar', {
      method: 'DELETE',
    });
    console.log('🌐 API: Avatar deleted successfully');
  }

  // Account settings methods
  async getAccountSettings(): Promise<any> {
    console.log('🌐 API: Requesting account settings...');
    const result = await this.request<any>('/account/settings');
    console.log('🌐 API: Account settings response:', result);
    return result;
  }

  async updateAccountSettings(settings: {
    emailNotifications: boolean;
    pushNotifications: boolean;
    weeklyReports: boolean;
    darkMode: boolean;
    language: string;
    timezone: string;
    twoFactorAuth: boolean;
  }): Promise<any> {
    console.log('🌐 API: Updating account settings...', settings);
    const result = await this.request<any>('/account/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    console.log('🌐 API: Account settings update response:', result);
    return result;
  }

  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<any> {
    console.log('🌐 API: Changing password...');
    const result = await this.request<any>('/account/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    console.log('🌐 API: Password change response:', result);
    return result;
  }

  // Chat methods
  async editChatMessage(channelId: string, messageId: string, content: string): Promise<any> {
    console.log('🌐 API: Editing chat message...', { channelId, messageId, content });
    return this.request<any>(`/chat/channels/${channelId}/messages/${messageId}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  async deleteChatMessage(channelId: string, messageId: string): Promise<void> {
    console.log('🌐 API: Deleting chat message...', { channelId, messageId });
    return this.request<void>(`/chat/channels/${channelId}/messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  async getChatChannels(): Promise<any[]> {
    return this.request<any[]>('/chat/channels');
  }

  async createChatChannel(channel: { name: string; description: string; type: string }): Promise<any> {
    console.log('🌐 API: Creating chat channel...', channel);
    const result = await this.request<any>('/chat/channels', {
      method: 'POST',
      body: JSON.stringify(channel),
    });
    console.log('🌐 API: Create channel response:', result);
    return result;
  }

  async getChatMessages(channelId: string, limit: number = 50, offset: number = 0): Promise<any[]> {
    console.log('🌐 API: Requesting chat messages for channel:', channelId);
    const result = await this.request<any[]>(`/chat/channels/${channelId}/messages?limit=${limit}&offset=${offset}`);
    console.log('🌐 API: Chat messages response:', result);
    return result;
  }

  async sendChatMessage(channelId: string, message: { content: string; messageType: string; replyTo?: string }): Promise<any> {
    console.log('🌐 API: Sending chat message...', message);
    const result = await this.request<any>(`/chat/channels/${channelId}/messages`, {
      method: 'POST',
      body: JSON.stringify(message),
    });
    console.log('🌐 API: Send message response:', result);
    return result;
  }

  async getDirectConversations(): Promise<any[]> {
    return this.request<any[]>('/chat/conversations');
  }

  async startDirectConversation(otherUserId: string): Promise<any> {
    console.log('🌐 API: Starting direct conversation with:', otherUserId);
    const result = await this.request<any>('/chat/conversations', {
      method: 'POST',
      body: JSON.stringify({ otherUserId }),
    });
    console.log('🌐 API: Start conversation response:', result);
    return result;
  }

  async getChatTeamMembers(): Promise<any[]> {
    return this.request<any[]>('/chat/team-members');
  }

  async updateChatChannel(channelId: string, updates: { name?: string; description?: string }): Promise<any> {
    console.log('🌐 API: Updating chat channel...', channelId, updates);
    const result = await this.request<any>(`/chat/channels/${channelId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    console.log('🌐 API: Update channel response:', result);
    return result;
  }

  async deleteChatChannel(channelId: string): Promise<void> {
    console.log('🌐 API: Deleting chat channel...', channelId);
    await this.request<void>(`/chat/channels/${channelId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Channel deleted successfully');
  }

  async getChatChannelMembers(channelId: string): Promise<any[]> {
    console.log('🌐 API: Requesting chat channel members...', channelId);
    const result = await this.request<any[]>(`/chat/channels/${channelId}/members`);
    console.log('🌐 API: Channel members response:', result);
    return result;
  }

  async addChatChannelMember(channelId: string, userIdToAdd: string, role: string = 'member'): Promise<any> {
    console.log('🌐 API: Adding chat channel member...', channelId, userIdToAdd, role);
    const result = await this.request<any>(`/chat/channels/${channelId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userIdToAdd, role }),
    });
    console.log('🌐 API: Add member response:', result);
    return result;
  }

  async removeChatChannelMember(channelId: string, memberId: string): Promise<void> {
    console.log('🌐 API: Removing chat channel member...', channelId, memberId);
    await this.request<void>(`/chat/channels/${channelId}/members/${memberId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Member removed successfully');
  }

  async getUnreadMessageCount(): Promise<{ count: number; channelCount: number; directCount: number }> {
    const result = await this.request<{ count: number; channelCount: number; directCount: number }>('/chat/unread-count');
    return result;
  }

  // Notification settings methods
  async getNotificationSettings(): Promise<{
    user_id: string;
    channel_email_notifications: boolean;
    dm_email_notifications: boolean;
    email_digest_interval: number;
  }> {
    console.log('🌐 API: Requesting notification settings...');
    const result = await this.request<any>('/chat/notification-settings');
    console.log('🌐 API: Notification settings response:', result);
    return result;
  }

  async updateNotificationSettings(settings: {
    channelEmailNotifications?: boolean;
    dmEmailNotifications?: boolean;
    emailDigestInterval?: number;
  }): Promise<any> {
    console.log('🌐 API: Updating notification settings...', settings);
    const result = await this.request<any>('/chat/notification-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    console.log('🌐 API: Notification settings update response:', result);
    return result;
  }

  async markMessagesAsRead(options?: { channelId?: string }): Promise<void> {
    console.log('🌐 API: Marking messages as read...', options);
    await this.request<void>('/chat/mark-read', {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
    console.log('🌐 API: Messages marked as read successfully');
  }

  async uploadChatFile(file: File, channelId: string, messageType?: string): Promise<any> {
    console.log('🌐 API: Uploading chat file...', { fileName: file.name, channelId, messageType });
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('channelId', channelId);
    if (messageType) {
      formData.append('messageType', messageType);
    }

    const token = this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/chat/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        // Don't set Content-Type for FormData, let browser set it with boundary
      },
      body: formData,
    });

    const data: ApiResponse = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Chat file upload failed');
    }

    console.log('🌐 API: Chat file upload response:', data.data);
    return data.data;
  }

  // Personalization methods
  async analyzeWebsite(url: string): Promise<any> {
    console.log('🌐 API: Analyzing website...', url);
    const result = await this.request<any>('/personalization/analyze-website', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
    console.log('🌐 API: Website analysis response:', result);
    return result;
  }

  async getPersonalizationSettings(): Promise<any> {
    console.log('🌐 API: Requesting personalization settings...');
    const result = await this.request<any>('/personalization/settings');
    console.log('🌐 API: Personalization settings response:', result);
    return result;
  }

  async updatePersonalizationSettings(settings: {
    theme?: any;
    branding?: any;
    customization?: any;
  }): Promise<any> {
    console.log('🌐 API: Updating personalization settings...', settings);
    const result = await this.request<any>('/personalization/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
    console.log('🌐 API: Personalization settings update response:', result);
    return result;
  }

  async generatePersonalizedTheme(websiteData: any): Promise<any> {
    console.log('🌐 API: Generating personalized theme...', websiteData);
    const result = await this.request<any>('/personalization/generate-theme', {
      method: 'POST',
      body: JSON.stringify({ websiteData }),
    });
    console.log('🌐 API: Theme generation response:', result);
    return result;
  }

  async analyzeLogo(logoUrl: string): Promise<any> {
    console.log('🌐 API: Analyzing logo...', logoUrl);
    const result = await this.request<any>('/personalization/analyze-logo', {
      method: 'POST',
      body: JSON.stringify({ logoUrl }),
    });
    console.log('🌐 API: Logo analysis response:', result);
    return result;
  }

  async generateBrandIcon(params: {
    companyName: string;
    industry?: string;
    style?: string;
    primaryColor?: string;
    isLightNavigation?: boolean;
  }): Promise<any> {
    console.log('🌐 API: Generating brand icon...', params);
    const result = await this.request<any>('/personalization/generate-brand-icon', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    console.log('🌐 API: Brand icon generation response:', result);
    return result;
  }

  // Admin methods - FAS 1.17
  async getAdminUsers(): Promise<any[]> {
    console.log('🌐 API: Requesting admin users list...');
    const result = await this.request<any[]>('/admin/users');
    console.log('🌐 API: Admin users response:', result);
    return result;
  }

  async getAdminUser(userId: string): Promise<any> {
    console.log('🌐 API: Requesting admin user details:', userId);
    const result = await this.request<any>(`/admin/users/${userId}`);
    console.log('🌐 API: Admin user response:', result);
    return result;
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<any> {
    console.log('🌐 API: Updating user status...', { userId, isActive });
    const result = await this.request<any>(`/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive }),
    });
    console.log('🌐 API: Update user status response:', result);
    return result;
  }

  async adminDeleteUser(userId: string): Promise<void> {
    console.log('🌐 API: Admin deleting user:', userId);
    await this.request<void>(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: User deleted successfully');
  }

  async getSystemStats(): Promise<any> {
    console.log('🌐 API: Requesting system stats...');
    const result = await this.request<any>('/admin/system-stats');
    console.log('🌐 API: System stats response:', result);
    return result;
  }

  async getAdminWorkspaces(): Promise<any[]> {
    console.log('🌐 API: Requesting admin workspaces...');
    const result = await this.request<any[]>('/admin/workspaces');
    console.log('🌐 API: Admin workspaces response:', result);
    return result;
  }

  async getActivityLogs(limit?: number): Promise<any[]> {
    console.log('🌐 API: Requesting activity logs...');
    const query = limit ? `?limit=${limit}` : '';
    const result = await this.request<any[]>(`/admin/activity-logs${query}`);
    console.log('🌐 API: Activity logs response:', result);
    return result;
  }

  async getDatabaseHealth(): Promise<any> {
    console.log('🌐 API: Requesting database health...');
    const result = await this.request<any>('/admin/database-health');
    console.log('🌐 API: Database health response:', result);
    return result;
  }

  // Team admin methods (legacy)
  async updateUserRole(userId: string, role: string, customPermissions?: string[]): Promise<any> {
    console.log('🌐 API: Updating user role...', { userId, role, customPermissions });
    const result = await this.request<any>('/team/role', {
      method: 'PUT',
      body: JSON.stringify({ userId, role, customPermissions }),
    });
    console.log('🌐 API: Update user role response:', result);
    return result;
  }

  async deleteUser(userId: string): Promise<void> {
    return this.request<void>(`/team/user/${userId}`, {
      method: 'DELETE',
    });
  }

  // Testing methods
  async getTestingComments(): Promise<any[]> {
    console.log('🌐 API: Requesting testing comments...');
    const result = await this.request<any[]>('/testing/comments');
    console.log('🌐 API: Testing comments response:', result);
    return result;
  }

  async getTestingCommentsByFeature(feature: string): Promise<any[]> {
    console.log('🌐 API: Requesting testing comments for feature:', feature);
    const result = await this.request<any[]>(`/testing/comments/feature/${encodeURIComponent(feature)}`);
    console.log('🌐 API: Testing comments by feature response:', result);
    return result;
  }

  async createTestingComment(comment: {
    feature: string;
    comment: string;
    priority: string;
  }): Promise<any> {
    console.log('🌐 API: Creating testing comment...', comment);
    const result = await this.request<any>('/testing/comments', {
      method: 'POST',
      body: JSON.stringify(comment),
    });
    console.log('🌐 API: Create testing comment response:', result);
    return result;
  }

  async updateTestingCommentStatus(id: string, status: string): Promise<any> {
    console.log('🌐 API: Updating testing comment status...', id, status);
    const result = await this.request<any>(`/testing/comments/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    console.log('🌐 API: Update testing comment status response:', result);
    return result;
  }

  async deleteTestingComment(id: string): Promise<void> {
    console.log('🌐 API: Deleting testing comment...', id);
    await this.request<void>(`/testing/comments/${id}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Testing comment deleted successfully');
  }

  async getTestingStats(): Promise<any> {
    console.log('🌐 API: Requesting testing stats...');
    const result = await this.request<any>('/testing/stats');
    console.log('🌐 API: Testing stats response:', result);
    return result;
  }

  async getTestingFeatures(): Promise<string[]> {
    console.log('🌐 API: Requesting testing features...');
    const result = await this.request<string[]>('/testing/features');
    console.log('🌐 API: Testing features response:', result);
    return result;
  }

  async createTestingCommentReply(commentId: string, reply: string): Promise<any> {
    console.log('🌐 API: Creating testing comment reply...', { commentId, reply });
    const result = await this.request<any>(`/testing/comments/${commentId}/replies`, {
      method: 'POST',
      body: JSON.stringify({ reply }),
    });
    console.log('🌐 API: Create testing comment reply response:', result);
    return result;
  }

  async getTestingCommentReplies(commentId: string): Promise<any[]> {
    console.log('🌐 API: Requesting testing comment replies...', commentId);
    const result = await this.request<any[]>(`/testing/comments/${commentId}/replies`);
    console.log('🌐 API: Testing comment replies response:', result);
    return result;
  }

  async deleteTestingCommentReply(replyId: string): Promise<void> {
    console.log('🌐 API: Deleting testing comment reply...', replyId);
    await this.request<void>(`/testing/replies/${replyId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Testing comment reply deleted successfully');
  }

  // Workspace methods
  async getCurrentWorkspace(): Promise<any> {
    console.log('🌐 API: Requesting current workspace...');
    const result = await this.request<any>('/workspace/current');
    console.log('🌐 API: Current workspace response:', result);
    return result;
  }

  async getWorkspaceMembers(): Promise<any[]> {
    console.log('🌐 API: Requesting workspace members...');
    const result = await this.request<any[]>('/workspace/members');
    console.log('🌐 API: Workspace members response:', result);
    return result;
  }

  // Multi-workspace methods
  async getMyWorkspaces(): Promise<{ workspaces: any[]; currentWorkspaceId: string }> {
    console.log('🌐 API: Requesting all my workspaces...');
    const url = `${API_BASE_URL}/workspace/my-workspaces`;
    const token = this.getAuthToken();

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    };

    const response = await fetch(url, config);
    const data: ApiResponse<any> = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to get workspaces');
    }

    console.log('🌐 API: My workspaces response:', data);
    return {
      workspaces: data.data || [],
      currentWorkspaceId: data.currentWorkspaceId || ''
    };
  }

  async switchWorkspace(workspaceId: string): Promise<any> {
    console.log('🌐 API: Switching workspace to:', workspaceId);
    const result = await this.request<any>(`/workspace/switch/${workspaceId}`, {
      method: 'POST',
    });
    console.log('🌐 API: Switch workspace response:', result);
    return result;
  }

  async createWorkspace(workspace: {
    name: string;
    companyWebsite?: string;
    industry?: string;
    companySize?: string;
  }): Promise<any> {
    console.log('🌐 API: Creating new workspace...', workspace);
    const result = await this.request<any>('/workspace', {
      method: 'POST',
      body: JSON.stringify(workspace),
    });
    console.log('🌐 API: Create workspace response:', result);
    return result;
  }

  async getWorkspaceNotificationsSummary(): Promise<{
    workspaces: Array<{ id: string; name: string; unread_messages: number; is_current: boolean }>;
    totalUnread: number;
    currentWorkspaceId: string;
  }> {
    console.log('🌐 API: Requesting workspace notifications summary...');
    const result = await this.request<any>('/workspace/notifications-summary');
    console.log('🌐 API: Notifications summary response:', result);
    return result;
  }

  async updateWorkspace(updates: {
    name?: string;
    companyWebsite?: string;
    industry?: string;
    companySize?: string;
    settings?: any;
  }): Promise<any> {
    console.log('🌐 API: Updating workspace...', updates);
    const result = await this.request<any>('/workspace', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    console.log('🌐 API: Workspace update response:', result);
    return result;
  }

  async inviteToWorkspace(email: string, role: string): Promise<any> {
    console.log('🌐 API: Inviting to workspace...', { email, role });
    const result = await this.request<any>('/workspace/invite', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
    console.log('🌐 API: Invite response:', result);
    return result;
  }

  async removeMemberFromWorkspace(userId: string): Promise<void> {
    console.log('🌐 API: Removing workspace member...', userId);
    await this.request<void>(`/workspace/members/${userId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Member removed successfully');
  }

  async updateMemberRole(userId: string, role: string): Promise<any> {
    console.log('🌐 API: Updating member role...', { userId, role });
    const result = await this.request<any>(`/workspace/members/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
    console.log('🌐 API: Update member role response:', result);
    return result;
  }

  // Filesystem methods
  async getFolders(path: string): Promise<string[]> {
    console.log('🌐 API: Requesting folders for path:', path);
    const result = await this.request<{ folders: string[] }>(`/filesystem/folders?path=${encodeURIComponent(path)}`);
    console.log('🌐 API: Folders response:', result);
    return result.folders;
  }

  async createFolder(path: string): Promise<any> {
    console.log('🌐 API: Creating folder:', path);
    const result = await this.request<any>('/filesystem/folders', {
      method: 'POST',
      body: JSON.stringify({ path }),
    });
    console.log('🌐 API: Create folder response:', result);
    return result;
  }

  async checkPathExists(path: string): Promise<boolean> {
    console.log('🌐 API: Checking if path exists:', path);
    const result = await this.request<{ exists: boolean }>(`/filesystem/exists?path=${encodeURIComponent(path)}`);
    console.log('🌐 API: Path exists response:', result);
    return result.exists;
  }

  // ============================================
  // MULTIPART UPLOAD METHODS (large files up to 500GB)
  // ============================================

  /**
   * Initiate a multipart upload for large files
   * Returns uploadId, signed URLs for chunks, and retention info
   */
  async initiateMultipartUpload(params: {
    fileName: string;
    fileSize: number;
    contentType: string;
    channelId: string;
  }): Promise<{
    uploadId: string;
    key: string;
    totalParts: number;
    chunkSize: number;
    partUrls: Array<{ partNumber: number; signedUrl: string }>;
    retentionDays: number | null;
    expiresAt: string | null;
    metadataId: string;
  }> {
    console.log('🌐 API: Initiating multipart upload...', params);
    const result = await this.request<any>('/chat/upload/initiate', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    console.log('🌐 API: Multipart upload initiated:', result);
    return result;
  }

  /**
   * Get additional signed URLs for more parts (for files with >100 chunks)
   */
  async getMorePartUrls(params: {
    uploadId: string;
    key: string;
    startPart: number;
    endPart: number;
  }): Promise<{
    partUrls: Array<{ partNumber: number; signedUrl: string }>;
  }> {
    console.log('🌐 API: Getting more part URLs...', params);
    const result = await this.request<any>('/chat/upload/get-part-urls', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    console.log('🌐 API: More part URLs response:', result);
    return result;
  }

  /**
   * Complete multipart upload after all parts are uploaded
   */
  async completeMultipartUpload(params: {
    uploadId: string;
    key: string;
    parts: Array<{ partNumber: number; etag: string }>;
    channelId: string;
  }): Promise<{
    message: any;
    retention: {
      retentionDays: number | null;
      expiresAt: string | null;
      isTemporary: boolean;
    };
  }> {
    console.log('🌐 API: Completing multipart upload...', params);
    const result = await this.request<any>('/chat/upload/complete', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    console.log('🌐 API: Multipart upload completed:', result);
    return result;
  }

  /**
   * Abort a multipart upload (cleanup)
   */
  async abortMultipartUpload(params: {
    uploadId: string;
    key: string;
  }): Promise<void> {
    console.log('🌐 API: Aborting multipart upload...', params);
    await this.request<void>('/chat/upload/abort', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    console.log('🌐 API: Multipart upload aborted');
  }

  /**
   * Get upload status for resume functionality
   */
  async getUploadStatus(uploadId: string): Promise<{
    uploadId: string;
    key: string;
    fileName: string;
    fileSize: number;
    totalParts: number;
    uploadedParts: number;
    status: 'in_progress' | 'completed' | 'aborted' | 'expired';
    retentionDays: number | null;
    expiresAt: string | null;
    parts: Array<{ partNumber: number; etag: string }>;
  }> {
    console.log('🌐 API: Getting upload status...', uploadId);
    const result = await this.request<any>(`/chat/upload/status/${uploadId}`);
    console.log('🌐 API: Upload status response:', result);
    return result;
  }

  // ============================================
  // PROJECT PLANNING METHODS
  // ============================================

  /**
   * Get all projects with planning overview
   */
  async getAllProjectsPlanning(): Promise<any[]> {
    console.log('🌐 API: Requesting all projects planning...');
    const result = await this.request<any[]>('/planning');
    console.log('🌐 API: All projects planning response:', result);
    return result;
  }

  /**
   * Get project planning data (phases, keystones, members with hours)
   */
  async getProjectPlanning(projectId: string): Promise<any> {
    console.log('🌐 API: Requesting project planning for:', projectId);
    const result = await this.request<any>(`/planning/${projectId}`);
    console.log('🌐 API: Project planning response:', result);
    return result;
  }

  /**
   * Create a new phase
   */
  async createPhase(phase: {
    projectId: string;
    name: string;
    startDate: string;
    endDate: string;
    status?: string;
    budgetTotal?: number;
  }): Promise<any> {
    console.log('🌐 API: Creating phase...', phase);
    const result = await this.request<any>('/planning/phases', {
      method: 'POST',
      body: JSON.stringify(phase),
    });
    console.log('🌐 API: Create phase response:', result);
    return result;
  }

  /**
   * Update a phase (dates, name, status, budget)
   */
  async updatePhase(phaseId: string, updates: {
    name?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    budgetTotal?: number;
  }): Promise<any> {
    console.log('🌐 API: Updating phase...', phaseId, updates);
    const result = await this.request<any>(`/planning/phases/${phaseId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    console.log('🌐 API: Update phase response:', result);
    return result;
  }

  /**
   * Delete a phase
   */
  async deletePhase(phaseId: string): Promise<void> {
    console.log('🌐 API: Deleting phase...', phaseId);
    await this.request<void>(`/planning/phases/${phaseId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Phase deleted successfully');
  }

  /**
   * Create a new keystone (milestone)
   */
  async createKeystone(keystone: {
    projectId: string;
    name: string;
    date: string;
    type?: string;
  }): Promise<any> {
    console.log('🌐 API: Creating keystone...', keystone);
    const result = await this.request<any>('/planning/keystones', {
      method: 'POST',
      body: JSON.stringify(keystone),
    });
    console.log('🌐 API: Create keystone response:', result);
    return result;
  }

  /**
   * Update a keystone (date, name, type)
   */
  async updateKeystone(keystoneId: string, updates: {
    name?: string;
    date?: string;
    type?: string;
  }): Promise<any> {
    console.log('🌐 API: Updating keystone...', keystoneId, updates);
    const result = await this.request<any>(`/planning/keystones/${keystoneId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    console.log('🌐 API: Update keystone response:', result);
    return result;
  }

  /**
   * Delete a keystone
   */
  async deleteKeystone(keystoneId: string): Promise<void> {
    console.log('🌐 API: Deleting keystone...', keystoneId);
    await this.request<void>(`/planning/keystones/${keystoneId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Keystone deleted successfully');
  }

  /**
   * Add member to phase
   */
  async addPhaseMember(member: {
    phaseId: string;
    userId: string;
    costRate?: number;
    billRate?: number;
    plannedStart?: string;
    plannedEnd?: string;
  }): Promise<any> {
    console.log('🌐 API: Adding phase member...', member);
    const result = await this.request<any>('/planning/members', {
      method: 'POST',
      body: JSON.stringify(member),
    });
    console.log('🌐 API: Add phase member response:', result);
    return result;
  }

  /**
   * Update phase member (dates, rates)
   */
  async updatePhaseMember(memberId: string, updates: {
    costRate?: number;
    billRate?: number;
    plannedStart?: string;
    plannedEnd?: string;
  }): Promise<any> {
    console.log('🌐 API: Updating phase member...', memberId, updates);
    const result = await this.request<any>(`/planning/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    console.log('🌐 API: Update phase member response:', result);
    return result;
  }

  /**
   * Remove member from phase
   */
  async removePhaseMember(memberId: string): Promise<void> {
    console.log('🌐 API: Removing phase member...', memberId);
    await this.request<void>(`/planning/members/${memberId}`, {
      method: 'DELETE',
    });
    console.log('🌐 API: Phase member removed successfully');
  }

  /**
   * Get current user's active phase assignments with progress
   */
  async getMyAssignments(): Promise<Array<{
    id: string;
    projectId: string;
    projectName: string;
    projectDescription: string;
    phaseId: string;
    phaseName: string;
    phaseStatus: string;
    startDate: string;
    endDate: string;
    plannedHours: number;
    actualHours: number;
    remainingHours: number;
    progress: number;
    costRate: number;
    billRate: number;
  }>> {
    console.log('🌐 API: Requesting my assignments...');
    const result = await this.request<any[]>('/planning/my-assignments');
    console.log('🌐 API: My assignments response:', result);
    return result;
  }

  // ============================================
  // AI PLANNING CHAT METHODS
  // ============================================

  /**
   * Get project context for AI planning
   */
  async getAIPlanningContext(projectId: string): Promise<any> {
    console.log('🌐 API: Getting AI planning context for:', projectId);
    const result = await this.request<any>(`/ai-planning/context/${projectId}`);
    console.log('🌐 API: AI planning context response:', result);
    return result;
  }

  /**
   * Send message to AI planning chat
   */
  async sendAIPlanningMessage(projectId: string, messages: Array<{ role: string; content: string }>, context: any): Promise<{
    message: string;
    suggestedPlan: { phases: any[]; keystones: any[] } | null;
  }> {
    console.log('🌐 API: Sending AI planning message...');
    const result = await this.request<any>(`/ai-planning/chat/${projectId}`, {
      method: 'POST',
      body: JSON.stringify({ messages, context }),
    });
    console.log('🌐 API: AI planning response:', result);
    return result;
  }

  /**
   * Accept AI-suggested plan (supports add/remove/update)
   */
  async acceptAIPlan(projectId: string, plan: {
    add?: any[];
    remove?: string[];
    update?: any[];
    phases?: any[];
    keystones?: any[];
  }): Promise<{
    added: any[];
    removed: string[];
    updated: any[];
    keystonesCreated: any[];
  }> {
    console.log('🌐 API: Accepting AI plan...', plan);
    const result = await this.request<any>(`/ai-planning/accept/${projectId}`, {
      method: 'POST',
      body: JSON.stringify(plan),
    });
    console.log('🌐 API: Accept plan response:', result);
    return result;
  }

  // ============================================
  // MULTIPART UPLOAD METHODS (large files up to 500GB)
  // ============================================

  /**
   * Upload a single chunk directly to R2 using the signed URL
   * Returns the ETag from the response headers
   */
  async uploadChunkToR2(signedUrl: string, chunk: Blob, onProgress?: (loaded: number, total: number) => void): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(event.loaded, event.total);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Get ETag from response headers (R2 returns it quoted)
          const etag = xhr.getResponseHeader('ETag');
          if (etag) {
            resolve(etag.replace(/"/g, '')); // Remove quotes
          } else {
            reject(new Error('No ETag in response'));
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed due to network error'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      xhr.open('PUT', signedUrl);
      xhr.send(chunk);
    });
  }
}

export const apiService = new ApiService();
